import { Optional, Component, OnInit, OnChanges, OnDestroy, Input, Output, EventEmitter, forwardRef } from '@angular/core';
import { ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { AbstractControl, FormGroup, FormGroupDirective } from '@angular/forms';
import { ControlContainer, ControlValueAccessor } from '@angular/forms';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { ModalController } from '@ionic/angular';
import { Subscription } from 'rxjs';

type TimeUnit = 'hour' | 'minute';

/**
 * Modal content for time selection.
 * Allows manual typing, increment/decrement controls and optional vertical swipe.
 */
@Component({
    selector: 'upp-time-modal',
    templateUrl: './upp-time-modal.html',
    styleUrls: ['./upp-time-modal.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class UppTimeModalComponent implements OnInit {
    @Input() value: string | null = null;
    @Input() title = '';
    @Input() placeholder = '';
    @Input() min: string | null = null;
    @Input() max: string | null = null;
    @Input() step = 1;
    @Input() swipeEnabled = true;
    @Input() swipeThreshold = 14;

    hourText = '00';
    minuteText = '00';

    private activeUnit: TimeUnit | null = null;
    private touchLastY = 0;
    private touchAccumulated = 0;

    constructor(private modalCtrl: ModalController, private change: ChangeDetectorRef) {
        // nothing to do
    }

    ngOnInit(): void {
        const time = this.NormalizeInputTime(this.value);
        this.hourText = this.ToTwoDigits(time.hour);
        this.minuteText = this.ToTwoDigits(time.minute);
        this.change.markForCheck();
    }

    get CanApply(): boolean {
        return this.GetCurrentTime() != null;
    }

    OnHourInput(event: Event | CustomEvent): void {
        this.hourText = this.ExtractNumericInput(event);
        this.change.markForCheck();
    }

    OnMinuteInput(event: Event | CustomEvent): void {
        this.minuteText = this.ExtractNumericInput(event);
        this.change.markForCheck();
    }

    OnHourBlur(): void {
        const current = this.GetCurrentTime() || this.GetFallbackTime();
        this.hourText = this.ToTwoDigits(current.hour);
        this.minuteText = this.ToTwoDigits(current.minute);
        this.change.markForCheck();
    }

    OnMinuteBlur(): void {
        const current = this.GetCurrentTime() || this.GetFallbackTime();
        this.hourText = this.ToTwoDigits(current.hour);
        this.minuteText = this.ToTwoDigits(current.minute);
        this.change.markForCheck();
    }

    IncrementHour(): void {
        this.AddToHour(1);
    }

    DecrementHour(): void {
        this.AddToHour(-1);
    }

    IncrementMinute(): void {
        this.AddToMinute(this.SafeStep);
    }

    DecrementMinute(): void {
        this.AddToMinute(-this.SafeStep);
    }

    OnTouchStart(unit: TimeUnit, event: TouchEvent): void {
        if (!this.swipeEnabled || event.touches.length === 0){
            return;
        }
        this.activeUnit = unit;
        this.touchLastY = event.touches[0].clientY;
        this.touchAccumulated = 0;
    }

    OnTouchMove(unit: TimeUnit, event: TouchEvent): void {
        if (!this.swipeEnabled || this.activeUnit !== unit || event.touches.length === 0){
            return;
        }

        const currentY = event.touches[0].clientY;
        this.touchAccumulated = this.touchAccumulated + (this.touchLastY - currentY);
        this.touchLastY = currentY;

        const threshold = this.SafeThreshold;
        while (Math.abs(this.touchAccumulated) >= threshold){
            if (this.touchAccumulated > 0){
                this.ApplySwipeStep(unit, 1);
                this.touchAccumulated = this.touchAccumulated - threshold;
            }
            else {
                this.ApplySwipeStep(unit, -1);
                this.touchAccumulated = this.touchAccumulated + threshold;
            }
        }
    }

    OnTouchEnd(): void {
        this.activeUnit = null;
        this.touchAccumulated = 0;
        this.touchLastY = 0;
    }

    async OnClose(): Promise<void> {
        await this.modalCtrl.dismiss(null);
    }

    async OnApply(): Promise<void> {
        const time = this.GetCurrentTime();
        if (!time){
            return;
        }
        await this.modalCtrl.dismiss(this.ToTimeText(time.hour, time.minute));
    }

    private ApplySwipeStep(unit: TimeUnit, direction: 1 | -1): void {
        if (unit === 'hour'){
            this.AddToHour(direction);
        }
        else {
            this.AddToMinute(direction * this.SafeStep);
        }
    }

    private AddToHour(delta: number): void {
        const current = this.GetCurrentTime() || this.GetFallbackTime();
        const nextHour = this.WrapNumber(current.hour + delta, 0, 23);
        this.ApplyTime(nextHour, current.minute);
    }

    private AddToMinute(delta: number): void {
        const current = this.GetCurrentTime() || this.GetFallbackTime();
        const currentTotal = (current.hour * 60) + current.minute;
        const next = this.WrapTotalMinutes(currentTotal + delta);
        this.ApplyTime(Math.floor(next / 60), next % 60);
    }

    private ApplyTime(hour: number, minute: number): void {
        const constrained = this.ConstrainToRange(hour, minute);
        this.hourText = this.ToTwoDigits(constrained.hour);
        this.minuteText = this.ToTwoDigits(constrained.minute);
        this.change.markForCheck();
    }

    private GetCurrentTime(): { hour: number; minute: number } | null {
        const hourRaw = this.ParsePartialNumber(this.hourText);
        const minuteRaw = this.ParsePartialNumber(this.minuteText);
        if (hourRaw == null || minuteRaw == null){
            return null;
        }
        if (hourRaw < 0 || hourRaw > 23 || minuteRaw < 0 || minuteRaw > 59){
            return null;
        }

        const minuteAligned = this.AlignMinuteToStep(minuteRaw);
        const constrained = this.ConstrainToRange(hourRaw, minuteAligned);
        return constrained;
    }

    private GetFallbackTime(): { hour: number; minute: number } {
        const minTime = this.ParseTime(this.min);
        if (minTime){
            return minTime;
        }
        return { hour: 0, minute: 0 };
    }

    private NormalizeInputTime(value: string | null): { hour: number; minute: number } {
        const parsed = this.ParseTime(value);
        if (parsed){
            return this.ConstrainToRange(parsed.hour, this.AlignMinuteToStep(parsed.minute));
        }
        return this.GetFallbackTime();
    }

    private ConstrainToRange(hour: number, minute: number): { hour: number; minute: number } {
        const total = (hour * 60) + minute;
        const min = this.MinTotal;
        const max = this.MaxTotal;

        let nextTotal = total;
        if (min != null && nextTotal < min){
            nextTotal = min;
        }
        if (max != null && nextTotal > max){
            nextTotal = max;
        }

        const nextHour = Math.floor(nextTotal / 60);
        const nextMinute = nextTotal % 60;
        return { hour: nextHour, minute: nextMinute };
    }

    private get MinTotal(): number | null {
        const min = this.ParseTime(this.min);
        return min ? ((min.hour * 60) + min.minute) : null;
    }

    private get MaxTotal(): number | null {
        const max = this.ParseTime(this.max);
        return max ? ((max.hour * 60) + max.minute) : null;
    }

    private ParseTime(value: string | null): { hour: number; minute: number } | null {
        if (!value){
            return null;
        }
        const clean = value.trim();
        const parts = clean.split(':');
        if (parts.length !== 2){
            return null;
        }

        const hour = Number(parts[0]);
        const minute = Number(parts[1]);
        if (!Number.isInteger(hour) || !Number.isInteger(minute)){
            return null;
        }
        if (hour < 0 || hour > 23 || minute < 0 || minute > 59){
            return null;
        }
        return { hour, minute };
    }

    private ParsePartialNumber(text: string): number | null {
        const clean = (text || '').replace(/\D/g, '');
        if (!clean){
            return null;
        }
        return Number(clean);
    }

    private ToTimeText(hour: number, minute: number): string {
        return this.ToTwoDigits(hour) + ':' + this.ToTwoDigits(minute);
    }

    private ToTwoDigits(value: number): string {
        return ('00' + value).slice(-2);
    }

    private WrapNumber(value: number, min: number, max: number): number {
        const range = (max - min) + 1;
        let next = value - min;
        next = ((next % range) + range) % range;
        return next + min;
    }

    private WrapTotalMinutes(value: number): number {
        const day = 24 * 60;
        let next = value;
        next = ((next % day) + day) % day;
        return next;
    }

    private AlignMinuteToStep(minute: number): number {
        const step = this.SafeStep;
        if (step <= 1){
            return minute;
        }
        const aligned = Math.round(minute / step) * step;
        if (aligned >= 60){
            return 60 - step;
        }
        return Math.max(0, aligned);
    }

    private ExtractNumericInput(event: Event | CustomEvent): string {
        const customValue = (event as CustomEvent).detail?.value;
        const targetValue = (event.target as HTMLInputElement)?.value;
        const value = (typeof customValue === 'string') ? customValue : (targetValue || '');
        return value.replace(/\D/g, '').slice(0, 2);
    }

    private get SafeStep(): number {
        const value = Math.floor(Number(this.step));
        if (!Number.isInteger(value) || value < 1 || value > 30){
            return 1;
        }
        return value;
    }

    private get SafeThreshold(): number {
        const value = Math.floor(Number(this.swipeThreshold));
        if (!Number.isInteger(value) || value < 4){
            return 14;
        }
        return value;
    }
}

/**
 * Time picker component for mobile and desktop.
 * Uses modal dialog with manual typing and increment/decrement controls.
 */
@Component({
    selector: 'upp-time-picker, upp-time',
    templateUrl: './upp-time.html',
    styleUrls: ['./upp-time.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => UppTimeComponent),
            multi: true
        }
    ]
})
export class UppTimeComponent implements ControlValueAccessor, OnInit, OnChanges, OnDestroy {
    @Input() formControlName = '';
    @Input() disabled = false;
    @Input() readonly = false;
    @Input() placeholder = '';
    @Input() title = '';
    @Input() icon = 'time-outline';
    @Input() min: string | null = null;
    @Input() max: string | null = null;
    @Input() step = 1;
    @Input() swipeEnabled = true;
    @Input() swipeThreshold = 14;
    @Output() Changed = new EventEmitter<string | null>();

    public formGroup: FormGroup | null = null;
    public formControl: AbstractControl | null = null;

    private _innerValue: string | null = null;
    private _formSubscription: Subscription | null = null;

    /* eslint-disable @typescript-eslint/no-unused-vars */
    private _onChangeCallback = (v: any) => {
        // nothing to do (required by angular)
    }
    private _onTouchCallback = () => {
        // nothing to do (required by angular)
    }
    /* eslint-enable @typescript-eslint/no-unused-vars */

    constructor(
        private modalCtrl: ModalController,
        private change: ChangeDetectorRef,
        @Optional() private controlContainer: ControlContainer
    ) {
        // nothing to do
    }

    ngOnInit(): void {
        if (this.controlContainer && this.controlContainer instanceof FormGroupDirective){
            this.formGroup = this.controlContainer.form;
        }

        this.formControl = (this.formGroup && this.formControlName) ? this.formGroup.get(this.formControlName) : null;
        if (this.formControl){
            this.value = this.NormalizeTime(this.formControl.value);
            this._formSubscription = this.formControl.valueChanges.subscribe(() => {
                this.value = this.NormalizeTime(this.formControl?.value);
            });
        }
    }

    ngOnChanges(): void {
        this.change.markForCheck();
    }

    ngOnDestroy(): void {
        if (this._formSubscription){
            this._formSubscription.unsubscribe();
            this._formSubscription = null;
        }
    }

    get value(): string | null {
        return this._innerValue;
    }

    set value(v: string | null) {
        const next = this.NormalizeTime(v);
        if (next !== this._innerValue){
            this._innerValue = next;
            this._onChangeCallback(next);
            this._onTouchCallback();
            this.Changed.emit(next);
            this.change.markForCheck();
        }
    }

    get ViewTitle(): string {
        return this.title || this.placeholder;
    }

    get IsDisabled(): boolean {
        if (this.disabled || this.readonly){
            return true;
        }
        if (this.formControl){
            return this.formControl.disabled;
        }
        return false;
    }

    async OnOpenPicker(): Promise<void> {
        if (this.IsDisabled){
            return;
        }

        const modal = await this.modalCtrl.create({
            component: UppTimeModalComponent,
            cssClass: 'modal-datetime',
            componentProps: {
                value: this.value,
                title: this.ViewTitle,
                placeholder: this.placeholder,
                min: this.min,
                max: this.max,
                step: this.step,
                swipeEnabled: this.swipeEnabled,
                swipeThreshold: this.swipeThreshold
            }
        });

        await modal.present();
        const result = await modal.onDidDismiss<string | null>();
        if (result.data != null){
            this.value = result.data;
        }
    }

    writeValue(value: string | null): void {
        const next = this.NormalizeTime(value);
        if (next !== this._innerValue){
            this._innerValue = next;
            this.change.markForCheck();
        }
    }

    registerOnChange(fn: any): void {
        this._onChangeCallback = fn;
    }

    registerOnTouched(fn: any): void {
        this._onTouchCallback = fn;
    }

    private NormalizeTime(value: any): string | null {
        if (typeof value !== 'string'){
            return null;
        }
        const parts = value.trim().split(':');
        if (parts.length !== 2){
            return null;
        }
        const hour = Number(parts[0]);
        const minute = Number(parts[1]);
        if (!Number.isInteger(hour) || !Number.isInteger(minute)){
            return null;
        }
        if (hour < 0 || hour > 23 || minute < 0 || minute > 59){
            return null;
        }
        return ('00' + hour).slice(-2) + ':' + ('00' + minute).slice(-2);
    }
}
